import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  useTheme,
  alpha,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Description as DocumentIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  PictureAsPdf as PictureAsPdfIcon,
  TableChart as ExcelIcon,
  Slideshow as PowerpointIcon,
  TextSnippet as TextIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearIcon,
  MoreVert as MoreIcon,
  Share as ShareIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  OpenInNew as OpenInNewIcon,
  AutoAwesome as AIIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import AIDocumentProcessingModal from './AIDocumentProcessingModal';

interface UserDocument {
  id: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  document_type: string;
  description?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  url?: string; // For display purposes
}

interface UserDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  onDocumentSelect?: (document: UserDocument) => void;
  mode?: 'view' | 'select';
  companyId?: string | null;
  companyName?: string | null;
  imagesOnly?: boolean; // New prop to filter only image files
}

// Document types
const DOCUMENT_TYPES = [
  { value: 'contract', label: 'Contract' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'license', label: 'License' },
  { value: 'passport', label: 'Passport' },
  { value: 'id_card', label: 'ID Card' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'tax_document', label: 'Tax Document' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'medical', label: 'Medical Record' },
  { value: 'education', label: 'Education Certificate' },
  { value: 'employment', label: 'Employment Document' },
  { value: 'other', label: 'Other' },
];

const UserDocumentsModal: React.FC<UserDocumentsModalProps> = ({ 
  open, 
  onClose, 
  onDocumentSelect,
  mode = 'view',
  companyId = null,
  companyName = null,
  imagesOnly = false
}) => {
  const theme = useTheme();
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Document type selection
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  
  // Enhanced document management
  const [editingDocument, setEditingDocument] = useState<UserDocument | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'size' | 'type'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // PDF Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<UserDocument | null>(null);
  
  // AI Processing state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiProcessingDocument, setAiProcessingDocument] = useState<UserDocument | null>(null);

  // Helper function to check if a file is an image
  const isImageFile = (fileType: string, fileName: string): boolean => {
    const imageMimeTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'image/tiff',
      'image/tif',
      'image/bmp',
      'image/svg+xml'
    ];
    
    const imageExtensions = [
      '.jpg', '.jpeg', '.png', '.gif', '.webp', '.tiff', '.tif', '.bmp', '.svg'
    ];
    
    // Check MIME type
    if (fileType && imageMimeTypes.includes(fileType.toLowerCase())) {
      return true;
    }
    
    // Check file extension as fallback
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  // Helper function to infer MIME type from file extension
  const inferMimeFromExtension = (ext: string): string => {
    const extMap: { [key: string]: string } = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'txt': 'text/plain',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    };
    return extMap[ext.toLowerCase()] || 'application/octet-stream';
  };

  // Debug function to help diagnose storage issues
  const debugStorageAccess = async () => {
    if (!user) return;
    
    console.log('🔍 Starting storage debug...');
    
    try {
      // Test 1: List root directory
      console.log('🔍 Test 1: Listing root directory');
      const { data: rootFiles, error: rootError } = await supabase.storage
        .from('user-documents')
        .list('', { limit: 100 });
      console.log('Root files:', rootFiles, 'Error:', rootError);
      
      // Test 2: List user directory (ACTUAL structure: {user_id}/)
      console.log('🔍 Test 2: Listing user directory (actual structure):', user.id);
      const { data: userFiles, error: userError } = await supabase.storage
        .from('user-documents')
        .list(user.id, { limit: 100 });
      console.log('User files:', userFiles, 'Error:', userError);
      
      // Test 3: List users/{user_id} directory (new structure)
      console.log('🔍 Test 3: Listing users/{user_id} directory (new structure)');
      const { data: newUserFiles, error: newUserError } = await supabase.storage
        .from('user-documents')
        .list(`users/${user.id}`, { limit: 100 });
      console.log('New user files:', newUserFiles, 'Error:', newUserError);
      
      // Test 4: Test signed URL creation for sample files
      if (userFiles && userFiles.length > 0) {
        const sampleFile = userFiles[0];
        console.log('🔍 Test 4a: Testing signed URL for user folder file:', sampleFile.name);
        const { data: urlData, error: urlError } = await supabase.storage
          .from('user-documents')
          .createSignedUrl(`${user.id}/${sampleFile.name}`, 3600);
        console.log('User folder signed URL result:', urlData, 'Error:', urlError);
        
        // Test 4b: Test download to verify file access
        console.log('🔍 Test 4b: Testing file download for:', `${user.id}/${sampleFile.name}`);
        const { data: downloadData, error: downloadError } = await supabase.storage
          .from('user-documents')
          .download(`${user.id}/${sampleFile.name}`);
        console.log('Download result:', downloadData ? 'SUCCESS' : 'FAILED', 'Error:', downloadError);
      }
      
      // Test 4b: Test signed URL creation for root files (reuse rootFiles from Test 1)
      if (rootFiles && rootFiles.length > 0) {
        const sampleRootFile = rootFiles[0];
        console.log('🔍 Test 4b: Testing signed URL for root file:', sampleRootFile.name);
        const { data: rootUrlData, error: rootUrlError } = await supabase.storage
          .from('user-documents')
          .createSignedUrl(sampleRootFile.name, 3600);
        console.log('Root file signed URL result:', rootUrlData, 'Error:', rootUrlError);
      }
      
      // Test 5: Check bucket policies
      console.log('🔍 Test 5: Checking bucket info');
      const { data: buckets, error: bucketError } = await supabase.storage.listBuckets();
      const userDocsBucket = buckets?.find(b => b.id === 'user-documents');
      console.log('User documents bucket:', userDocsBucket, 'Error:', bucketError);
      
      toast.success('Storage debug completed. Check console for details.');
    } catch (error) {
      console.error('Debug error:', error);
      toast.error('Debug failed. Check console for details.');
    }
  };

  // Load user documents
  const loadDocuments = async () => {
    if (!user) {
      console.error('❌ No authenticated user found');
      setError('Please log in to view your documents');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Loading documents for user:', user.id, companyId ? `(Company mode: ${companyId})` : '(Personal mode)');
      
      let dbDocs: any[] = [];
      
      // Try to fetch from documents table first (if it exists)
      try {
        console.log('🔍 Checking if documents table exists...');
        const { data: tableCheck, error: tableError } = await supabase
          .from('documents')
          .select('id')
          .limit(1);

        if (tableError) {
          console.warn('⚠️ Documents table error:', tableError.message);
          if (tableError.message.includes('relation "documents" does not exist')) {
            console.log('📝 Documents table does not exist - will fetch from storage only');
          } else if (tableError.message.includes('permission denied')) {
            console.log('🔒 Permission denied for documents table - will fetch from storage only');
          } else {
            throw tableError;
          }
        } else {
          console.log('✅ Documents table accessible');
          
          // Fetch documents from the documents table
          let query = supabase
            .from('documents')
            .select('*');

          if (companyId) {
            // In company mode, ONLY show documents associated with this company
            console.log('🏢 Company mode: filtering by company_id:', companyId);
            query = query.eq('company_id', companyId);
          } else {
            // In personal mode, show documents belonging to the user (no company_id)
            console.log('👤 Personal mode: filtering by user_id:', user.id);
            query = query.eq('user_id', user.id).is('company_id', null);
          }
          
          const { data, error } = await query.order('created_at', { ascending: false });

          if (error) {
            console.warn('⚠️ Error fetching from documents table:', error.message);
            
            // Check if the error is about missing company_id column
            if (error.message && error.message.includes('company_id')) {
              console.log('📝 Company ID column not available yet - will fetch from storage only');
            } else if (error.message && error.message.includes('row-level security')) {
              console.log('🔒 RLS policy issue - will fetch from storage only');
            } else {
              throw error;
            }
          } else {
            dbDocs = Array.isArray(data) ? data : [];
            console.log(`📄 Documents from database (${companyId ? 'Company' : 'Personal'} mode):`, dbDocs.length);
          }
        }
      } catch (dbError) {
        console.warn('⚠️ Database fetch failed, continuing with storage-only approach:', dbError);
      }

      // Fetch from storage bucket based on mode
      let storageFiles: any[] = [];
      if (!companyId) {
        // In personal mode, check both old and new folder structures
        console.log('📁 Fetching from personal storage folders...');
        
        // Try new folder structure first: users/{user_id}
        console.log('📁 Checking new structure: users/' + user.id);
        const { data: newStructureFiles, error: newStructureError } = await supabase.storage
          .from('user-documents')
          .list(`users/${user.id}`, { limit: 1000, offset: 0 });

        if (newStructureError) {
          console.warn('⚠️ Could not list new structure files:', newStructureError.message);
        } else {
          console.log('✅ New structure files found:', newStructureFiles?.length || 0);
        }

        // Try user folder structure: {user_id} (this is the ACTUAL structure based on your images)
        console.log('📁 Checking user folder structure: ' + user.id);
        const { data: userFolderFiles, error: userFolderError } = await supabase.storage
          .from('user-documents')
          .list(user.id, { limit: 1000, offset: 0 });

        if (userFolderError) {
          console.warn('⚠️ Could not list user folder files:', userFolderError.message);
        } else {
          console.log('✅ User folder files found:', userFolderFiles?.length || 0);
          if (userFolderFiles?.length > 0) {
            console.log('Sample user folder files:', userFolderFiles.slice(0, 5).map(f => f.name));
          }
        }

        // Combine files from both structures
        const newFiles = newStructureFiles || [];
        const userFiles = userFolderFiles || [];
        
        // Add path prefix to user folder files for consistency
        const userFilesWithPath = userFiles.map((f: any) => ({
          ...f,
          originalPath: `${user.id}/${f.name}` // User folder path
        }));
        
        storageFiles = [...newFiles, ...userFilesWithPath];
        console.log('🗂️ Total personal storage files found:', storageFiles.length);
      } else {
        // In company mode, list files from company folder (NO user subfolder)
        console.log('📁 Fetching from company storage folder (companies/{company_id})');
        const { data: companyStorageFiles, error: companyListError } = await supabase.storage
          .from('user-documents')
          .list(`companies/${companyId}`, { limit: 1000, offset: 0 });

        if (companyListError) {
          console.warn('⚠️ Could not list company storage files:', companyListError.message);
        } else {
          console.log('✅ Company storage files found:', companyStorageFiles?.length || 0);
        }

        storageFiles = companyStorageFiles || [];
      }

      const existingPaths = new Set((dbDocs || []).map((d: any) => d.file_path));
      console.log('📋 Existing database paths:', Array.from(existingPaths));

      const synthesizedFromStorage = (storageFiles || [])
        .filter((f: any) => f?.name && f.name !== '.emptyFolderPlaceholder')
        .map((f: any) => {
          const name: string = f.name;
          const ext = (name.split('.').pop() || '').toLowerCase();
          const mime = inferMimeFromExtension(ext);
          
          // Create the correct file path based on mode and structure
          let filePath: string;
          if (companyId) {
            filePath = `companies/${companyId}/${f.name}`; // NO user subfolder for company docs
          } else {
            // Check if this is from user folder structure (has originalPath)
            if (f.originalPath) {
              filePath = f.originalPath; // Use user folder path: {user_id}/filename
            } else {
              filePath = `${user.id}/${f.name}`; // Use user folder path for new files
            }
          }
          
          // Check if this path already exists in database
          if (existingPaths.has(filePath)) {
            return null; // Skip if already in database
          }
          
          return {
            // Synthesize a UserDocument-like shape for display
            id: filePath,
            user_id: user.id,
            file_name: f.name,
            file_path: filePath,
            file_size: (f as any).metadata?.size ?? (f.size ?? 0),
            file_type: mime,
            document_type: 'other',
            description: null,
            is_public: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
        })
        .filter(Boolean); // Remove null entries

      console.log('🔄 Synthesized from storage:', synthesizedFromStorage.length, synthesizedFromStorage);

      const combined = [...dbDocs, ...synthesizedFromStorage];
      console.log('📦 Combined documents:', combined.length, combined);
      console.log('📋 Document paths breakdown:');
      combined.forEach((doc, index) => {
        console.log(`  ${index + 1}. ${doc.file_name} -> ${doc.file_path}`);
      });

      // Get signed URLs for each document
      const documentsWithUrls = await Promise.all(
        combined.map(async (doc: any) => {
          try {
            console.log(`🔗 Creating signed URL for: ${doc.file_path}`);
            
            // First, verify the file exists in storage
            const { data: fileData, error: fileError } = await supabase.storage
              .from('user-documents')
              .download(doc.file_path);
            
            if (fileError) {
              console.warn(`❌ File not accessible for ${doc.file_path}:`, fileError);
              // Try alternative path structures
              const alternativePaths = [
                doc.file_path.replace('users/', ''), // Remove users/ prefix
                doc.file_path.replace(/^users\/[^\/]+\//, ''), // Remove users/{user_id}/ prefix
                doc.file_name, // Just the filename
                // Try the actual user folder structure: {user_id}/filename
                `${user.id}/${doc.file_name}`,
                // Try removing any prefix and just use user_id/filename
                doc.file_path.split('/').slice(-2).join('/') // Get last two parts (user_id/filename)
              ];
              
              for (const altPath of alternativePaths) {
                if (altPath === doc.file_path) continue; // Skip if same as original
                
                console.log(`🔄 Trying alternative path: ${altPath}`);
                const { data: altFileData, error: altFileError } = await supabase.storage
                  .from('user-documents')
                  .download(altPath);
                
                if (!altFileError) {
                  console.log(`✅ Found file at alternative path: ${altPath}`);
                  // Update the document's file_path to the working path
                  doc.file_path = altPath;
                  break;
                }
              }
            }
            
            // Now create the signed URL
            const { data: urlData, error: signedErr } = await supabase.storage
              .from('user-documents')
              .createSignedUrl(doc.file_path, 3600);
            
            if (signedErr) {
              console.warn(`❌ Could not create signed URL for ${doc.file_path}:`, signedErr);
              console.warn(`❌ Signed URL error details:`, {
                message: signedErr.message,
                name: signedErr.name,
                status: (signedErr as any).status || 'unknown'
              });
              return null;
            }
            
            console.log(`✅ Created signed URL for ${doc.file_name}:`, urlData?.signedUrl);
            return {
              ...doc,
              url: urlData?.signedUrl || ''
            };
          } catch (err) {
            console.warn(`❌ Error creating signed URL for ${doc.file_path}:`, err);
            return null;
          }
        })
      );

      // Filter out any null results (files that couldn't be accessed)
      let validDocuments = documentsWithUrls.filter(Boolean);
      
      // If imagesOnly is true, filter to show only image files
      if (imagesOnly) {
        validDocuments = validDocuments.filter(doc => 
          isImageFile(doc.file_type, doc.file_name)
        );
        console.log('🖼️ Filtered to image files only:', validDocuments.length);
      }
      
      console.log('✅ Final valid documents:', validDocuments.length, validDocuments);
      
      if (validDocuments.length === 0) {
        if (imagesOnly) {
          console.log('⚠️ No image files found. This could mean:');
          console.log('1. No image files have been uploaded yet');
          console.log('2. Only non-image documents are available');
          console.log('3. Storage policies are blocking access to image files');
          
          // Check if there are any documents at all
          const allDocuments = documentsWithUrls.filter(Boolean);
          if (allDocuments.length > 0) {
            setError('No image files found in your documents. Please upload some image files (JPG, PNG, GIF, WebP, etc.) to use this feature.');
          } else {
            setError('No documents found. Please upload some image files (JPG, PNG, GIF, WebP, etc.) to use this feature.');
          }
        } else {
          console.log('⚠️ No documents found. This could mean:');
          console.log('1. No documents have been uploaded yet');
          console.log('2. Documents are in a different folder structure');
          console.log('3. Storage policies are blocking access');
          console.log('4. Database migration needs to be run');
          
          // Provide more specific error messages based on what was found
          if (dbDocs.length > 0 && storageFiles.length > 0) {
            setError('Documents found in both database and storage but could not be accessed. This indicates a storage permissions issue. Please check your Supabase storage policies.');
          } else if (dbDocs.length > 0) {
            setError('Documents found in database but could not be accessed from storage. The files may have been moved or deleted from storage.');
          } else if (storageFiles.length > 0) {
            setError('Documents found in storage but could not be accessed. This indicates a storage permissions issue. Please check your Supabase storage policies.');
          } else {
            console.log('📝 No documents found - this is normal for new users');
          }
        }
      }
      
      setDocuments(validDocuments);
      console.log('✅ Successfully loaded documents:', validDocuments.length);
    } catch (err: any) {
      console.error('❌ Error loading documents:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to load documents';
      
      if (err?.message) {
        if (err.message.includes('relation "documents" does not exist')) {
          errorMessage = 'Documents table not found. Please run the database migration.';
        } else if (err.message.includes('permission denied')) {
          errorMessage = 'Permission denied. Please check your authentication.';
        } else if (err.message.includes('row-level security')) {
          errorMessage = 'Access denied. Please check your permissions.';
        } else if (err.message.includes('company_id')) {
          errorMessage = 'Company documents feature not available yet. Please run the database migration.';
        } else {
          errorMessage = `Failed to load documents: ${err.message}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Upload file
  const uploadFile = async (file: File, documentType: string, description: string, isPublic: boolean = false) => {
    if (!user) {
      console.error('❌ No authenticated user found for upload');
      setError('Please log in to upload documents');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      console.log('🚀 Starting document upload:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        documentType,
        companyId: companyId || 'personal',
        userId: user.id
      });

      // Preserve original filename but sanitize it for storage
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      
      // Create different storage paths for personal vs company documents
      let filePath: string;
      if (companyId) {
        // Company documents: store in company-specific folder (NO user subfolder)
        filePath = `companies/${companyId}/${sanitizedFileName}`;
        console.log('🏢 Company mode: Upload path:', filePath);
      } else {
        // Personal documents: store in user's personal folder
        filePath = `${user.id}/${sanitizedFileName}`;
        console.log('👤 Personal mode: Upload path:', filePath);
      }

      // Upload file to storage
      console.log('⬆️ Uploading to storage bucket...');
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('user-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Storage upload error:', uploadError);
        throw new Error(`Storage upload failed: ${uploadError.message}`);
      }

      console.log('✅ File uploaded to storage:', uploadData);

      // Save document metadata to documents table
      const documentData: any = {
        user_id: user.id,
        file_name: file.name, // Keep original filename for display
        file_path: filePath, // Use sanitized filename for storage path
        file_size: file.size,
        file_type: file.type,
        document_type: documentType,
        description: description || null,
        is_public: isPublic
      };

      // Only include company_id if it exists and we're in company mode
      if (companyId) {
        documentData.company_id = companyId;
        console.log('🏢 Company mode: Adding company_id:', companyId);
      } else {
        console.log('👤 Personal mode: No company_id');
      }

      console.log('💾 Saving document metadata:', documentData);

      let insertedData: any;
      const { data: initialData, error: documentError } = await supabase
        .from('documents')
        .insert(documentData)
        .select()
        .single();

      if (documentError) {
        console.error('❌ Document insert error:', documentError);
        
        // Check if the error is about missing company_id column
        if (documentError.message && documentError.message.includes('company_id')) {
          console.warn('⚠️ Company ID column not available yet. Uploading as personal document.');
          // Remove company_id and try again
          delete documentData.company_id;
          const { data: retryData, error: retryError } = await supabase
            .from('documents')
            .insert(documentData)
            .select()
            .single();
          
          if (retryError) {
            console.error('❌ Retry insert error:', retryError);
            throw new Error(`Document metadata save failed: ${retryError.message}`);
          }
          insertedData = retryData;
        } else {
          throw new Error(`Document metadata save failed: ${documentError.message}`);
        }
      } else {
        insertedData = initialData;
        console.log('✅ Document metadata saved:', insertedData);
      }

      // Get signed URL for the uploaded file
      console.log('🔗 Creating signed URL...');
      const { data: urlData, error: urlError } = await supabase.storage
        .from('user-documents')
        .createSignedUrl(filePath, 3600);

      if (urlError) {
        console.warn('⚠️ Could not create signed URL:', urlError);
      }

      const newDocument: UserDocument = {
        ...insertedData,
        url: urlData?.signedUrl || ''
      };

      console.log('✅ Document upload complete:', newDocument);
      setDocuments(prev => [newDocument, ...prev]);
      toast.success('Document uploaded successfully!');
    } catch (err: any) {
      console.error('❌ Upload error:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to upload document';
      
      if (err?.message) {
        if (err.message.includes('Storage upload failed')) {
          errorMessage = `Storage upload failed: ${err.message}`;
        } else if (err.message.includes('Document metadata save failed')) {
          errorMessage = `Database save failed: ${err.message}`;
        } else if (err.message.includes('permission denied')) {
          errorMessage = 'Permission denied. Please check your authentication.';
        } else if (err.message.includes('row-level security')) {
          errorMessage = 'Access denied. Please check your permissions.';
        } else if (err.message.includes('company_id')) {
          errorMessage = 'Company documents feature not available yet. Please run the database migration.';
        } else {
          errorMessage = `Upload failed: ${err.message}`;
        }
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Delete document
  const deleteDocument = async (userDocument: UserDocument) => {
    if (!user) return;

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('user-documents')
        .remove([userDocument.file_path]);

      if (storageError) throw storageError;

      // Delete from documents table
      const { error: tableError } = await supabase
        .from('documents')
        .delete()
        .eq('id', userDocument.id);

      if (tableError) throw tableError;

      setDocuments(prev => prev.filter(doc => doc.id !== userDocument.id));
      toast.success('Document deleted successfully!');
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error('Failed to delete document');
    }
  };

  // Download document
  const downloadDocument = (userDocument: UserDocument) => {
    const link = document.createElement('a');
    link.href = userDocument.url || '';
    link.download = userDocument.file_name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // View document
  const viewDocument = async (document: UserDocument) => {
    if (!document.url) {
      toast.error('Document URL not available');
      return;
    }

    try {
      // Check if this is a PDF
      const isPdf = document.file_type === 'application/pdf' || document.file_name.toLowerCase().endsWith('.pdf');
      
      if (isPdf) {
        // For PDFs, open in PDF viewer popup
        setViewingDocument(document);
        setViewerOpen(true);
        toast.success('Opening PDF viewer');
      } else {
        // For other files, try to open in new tab
        // If it's a Supabase storage URL, we might need to create a fresh signed URL
        let viewUrl = document.url;
        
        if (document.url.includes('supabase.co') && document.url.includes('/storage/')) {
          try {
            // Extract file path and create fresh signed URL
            const urlParts = document.url.split('/storage/v1/object/');
            if (urlParts.length > 1) {
              const fullPath = urlParts[1];
              const pathParts = fullPath.split('/');
              const bucket = pathParts[0];
              const filePath = pathParts.slice(1).join('/');
              
              const { data: urlData, error } = await supabase.storage
                .from(bucket)
                .createSignedUrl(filePath, 3600);
              
              if (error) {
                console.warn('Could not create fresh signed URL, using original:', error);
              } else if (urlData?.signedUrl) {
                viewUrl = urlData.signedUrl;
              }
            }
          } catch (err) {
            console.warn('Error creating fresh signed URL:', err);
          }
        }
        
        // Open in new tab
        window.open(viewUrl, '_blank');
        toast.success('Opening document in new tab');
      }
    } catch (err) {
      console.error('Error viewing document:', err);
      toast.error('Failed to open document');
    }
  };

  // Handle file drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setShowUploadForm(true);
    }
  };

  // Handle file input change
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      setShowUploadForm(true);
    }
  };

  // Handle upload form submission
  const handleUploadSubmit = () => {
    if (selectedFile && documentType) {
      uploadFile(selectedFile, documentType, description, isPublic);
      setShowUploadForm(false);
      setSelectedFile(null);
      setDocumentType('');
      setDescription('');
      setIsPublic(false);
    }
  };

  // Cancel upload
  const handleUploadCancel = () => {
    setShowUploadForm(false);
    setSelectedFile(null);
    setDocumentType('');
    setDescription('');
    setIsPublic(false);
  };

  // Get file icon based on type
  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return <PdfIcon color="error" />;
    if (type.includes('image')) return <ImageIcon color="primary" />;
    if (type.includes('excel') || type.includes('spreadsheet')) return <ExcelIcon color="success" />;
    if (type.includes('powerpoint') || type.includes('presentation')) return <PowerpointIcon color="warning" />;
    if (type.includes('text')) return <TextIcon color="info" />;
    return <DocumentIcon color="action" />;
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get document type label
  const getDocumentTypeLabel = (type: string) => {
    const docType = DOCUMENT_TYPES.find(dt => dt.value === type);
    return docType ? docType.label : type;
  };

  // Enhanced document management functions
  const handleEditDocument = (document: UserDocument) => {
    setEditingDocument(document);
    setDocumentType(document.document_type);
    setDescription(document.description || '');
    setIsPublic(document.is_public || false);
  };

  const handleAIDocumentProcessing = (document: UserDocument) => {
    setAiProcessingDocument(document);
    setAiModalOpen(true);
  };

  const handleUpdateDocument = async () => {
    if (!editingDocument) return;
    
    try {
      const { error } = await supabase
        .from('documents')
        .update({
          document_type: documentType,
          description: description,
          is_public: isPublic,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingDocument.id);

      if (error) throw error;

      toast.success('Document updated successfully');
      setEditingDocument(null);
      setDocumentType('');
      setDescription('');
      loadDocuments();
    } catch (err) {
      console.error('Error updating document:', err);
      toast.error('Failed to update document');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments.length === 0) return;
    
    try {
      // Delete from database
      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .in('id', selectedDocuments);

      if (dbError) throw dbError;

      // Delete from storage
      for (const docId of selectedDocuments) {
        const doc = documents.find(d => d.id === docId);
        if (doc?.file_path) {
          await supabase.storage
            .from('user-documents')
            .remove([doc.file_path]);
        }
      }

      toast.success(`${selectedDocuments.length} documents deleted successfully`);
      setSelectedDocuments([]);
      setShowBulkActions(false);
      loadDocuments();
    } catch (err) {
      console.error('Error deleting documents:', err);
      toast.error('Failed to delete documents');
    }
  };

  const handleBulkUpdateType = async (newType: string) => {
    if (selectedDocuments.length === 0) return;
    
    try {
      const { error } = await supabase
        .from('documents')
        .update({
          document_type: newType,
          updated_at: new Date().toISOString()
        })
        .in('id', selectedDocuments);

      if (error) throw error;

      toast.success(`${selectedDocuments.length} documents updated successfully`);
      setSelectedDocuments([]);
      setShowBulkActions(false);
      loadDocuments();
    } catch (err) {
      console.error('Error updating documents:', err);
      toast.error('Failed to update documents');
    }
  };

  const toggleDocumentSelection = (docId: string) => {
    setSelectedDocuments(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const selectAllDocuments = () => {
    setSelectedDocuments(documents.map(doc => doc.id));
  };

  const clearSelection = () => {
    setSelectedDocuments([]);
  };

  // Filter and sort documents
  const filteredAndSortedDocuments = documents
    .filter(doc => {
      const matchesType = filterType === 'all' || doc.document_type === filterType;
      const matchesSearch = searchQuery === '' || 
        doc.file_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.description && doc.description.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesType && matchesSearch;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.file_name.localeCompare(b.file_name);
          break;
        case 'date':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'size':
          comparison = a.file_size - b.file_size;
          break;
        case 'type':
          comparison = a.document_type.localeCompare(b.document_type);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Load documents when modal opens
  useEffect(() => {
    if (open && user) {
      loadDocuments();
    }
  }, [open, user]);

  // Handle document selection for select mode
  const handleDocumentSelect = (document: UserDocument) => {
    if (onDocumentSelect) {
      onDocumentSelect(document);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '600px',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {companyId && companyName ? (
            <BusinessIcon color="primary" />
          ) : (
            <DocumentIcon color="primary" />
          )}
          <Typography variant="h6" fontWeight="bold">
            {mode === 'select' ? (imagesOnly ? 'Select Image' : 'Select Document') : 
             companyId && companyName ? `My Documents - ${companyName}` : 'My Documents'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Upload Area - Only show in view mode */}
        {mode === 'view' && (
          <Box sx={{ p: 3, pb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              border: `2px dashed ${dragActive ? theme.palette.primary.main : theme.palette.divider}`,
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backgroundColor: dragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
              }
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xls,.xlsx,.ppt,.pptx"
              onChange={handleFileInput}
            />
            
            <motion.div
              animate={{ scale: dragActive ? 1.05 : 1 }}
              transition={{ duration: 0.2 }}
            >
              <UploadIcon 
                sx={{ 
                  fontSize: 48, 
                  color: dragActive ? 'primary.main' : 'text.secondary',
                  mb: 2 
                }} 
              />
              <Typography variant="h6" gutterBottom>
                {dragActive ? 'Drop your file here' : 'Upload Documents'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Drag and drop files here, or click to select files
              </Typography>
              <Chip 
                label="PDF, DOC, DOCX, TXT, Images, Excel, PowerPoint" 
                size="small" 
                variant="outlined"
              />
            </motion.div>
          </Paper>

          {/* Upload Progress */}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress 
                variant="indeterminate" 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                }} 
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Uploading document...
              </Typography>
            </Box>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
        )}

        {/* Upload Form */}
        {showUploadForm && selectedFile && (
          <Box sx={{ p: 3, pt: 0 }}>
            <Card sx={{ borderRadius: 2, border: 1, borderColor: 'primary.main' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Upload Document
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Selected file: <strong>{selectedFile.name}</strong> ({formatFileSize(selectedFile.size)})
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Document Type</InputLabel>
                      <Select
                        value={documentType}
                        onChange={(e) => setDocumentType(e.target.value)}
                        label="Document Type"
                      >
                        {DOCUMENT_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Select the type of document</FormHelperText>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label="Description (Optional)"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Add a description for this document"
                    />
                  </Box>
                </Box>

                {/* Public/Private Toggle for Upload */}
                <Box sx={{ mt: 3 }}>
                  <Card sx={{ 
                    p: 2, 
                    bgcolor: isPublic ? 'rgba(76, 175, 80, 0.08)' : 'rgba(158, 158, 158, 0.08)',
                    border: `1px solid ${isPublic ? 'rgba(76, 175, 80, 0.2)' : 'rgba(158, 158, 158, 0.2)'}`,
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: isPublic ? 'success.main' : 'grey.500',
                        color: 'white',
                        transition: 'all 0.3s ease-in-out'
                      }}>
                        {isPublic ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ 
                          color: isPublic ? 'success.main' : 'grey.600',
                          transition: 'color 0.3s ease-in-out'
                        }}>
                          {isPublic ? 'Make this document public' : 'Keep this document private'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {isPublic 
                            ? 'Others can see this in your profile' 
                            : 'Only you can see this document'
                          }
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                            color="success"
                            size="small"
                            sx={{
                              '& .MuiSwitch-thumb': {
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              },
                              '& .MuiSwitch-track': {
                                backgroundColor: isPublic ? 'success.main' : 'grey.400',
                              },
                            }}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </Card>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleUploadCancel}
                    disabled={uploading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleUploadSubmit}
                    disabled={!documentType || uploading}
                    startIcon={<UploadIcon />}
                  >
                    Upload Document
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        <Divider />

        {/* Documents List */}
        <Box sx={{ p: 3, pt: 2 }}>
          {/* Enhanced Toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Your Documents ({filteredAndSortedDocuments.length})
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Debug Button */}
              <Tooltip title="Debug storage access">
                <IconButton size="small" onClick={debugStorageAccess} sx={{ color: 'warning.main' }}>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
              
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ width: 200 }}
              />
              
              {/* Filter */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {DOCUMENT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Sort */}
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="size">Size</MenuItem>
                  <MenuItem value="type">Type</MenuItem>
                </Select>
              </FormControl>
              
              {/* Sort Order */}
              <IconButton
                size="small"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                <SortIcon sx={{ transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none' }} />
              </IconButton>
            </Box>
          </Box>

          {/* Bulk Actions - Only show in view mode */}
          {mode === 'view' && selectedDocuments.length > 0 && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              p: 2, 
              bgcolor: 'primary.50', 
              borderRadius: 2, 
              mb: 2 
            }}>
              <Typography variant="body2" color="primary">
                {selectedDocuments.length} document(s) selected
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleBulkDelete}
                startIcon={<DeleteIcon />}
                color="error"
              >
                Delete
              </Button>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value=""
                  onChange={(e) => handleBulkUpdateType(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Change Type</MenuItem>
                  {DOCUMENT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="text"
                onClick={clearSelection}
                startIcon={<ClearIcon />}
              >
                Clear
              </Button>
            </Box>
          )}

          {/* Select All / Clear All - Only show in view mode */}
          {mode === 'view' && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={selectAllDocuments}
                startIcon={<SelectAllIcon />}
                disabled={documents.length === 0}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={clearSelection}
                startIcon={<ClearIcon />}
                disabled={selectedDocuments.length === 0}
              >
                Clear Selection
              </Button>
            </Box>
          )}

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading documents...
              </Typography>
            </Box>
          ) : documents.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No documents yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload your first document to get started
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              <AnimatePresence>
                {filteredAndSortedDocuments.map((document, index) => (
                  <motion.div
                    key={document.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <ListItem
                      sx={{
                        border: 1,
                        borderColor: selectedDocuments.includes(document.id) ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        mb: 1,
                        backgroundColor: selectedDocuments.includes(document.id) ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                        cursor: mode === 'select' ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        }
                      }}
                      onClick={mode === 'select' ? () => handleDocumentSelect(document) : undefined}
                    >
                      <ListItemIcon>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {mode === 'view' && (
                            <input
                              type="checkbox"
                              checked={selectedDocuments.includes(document.id)}
                              onChange={() => toggleDocumentSelection(document.id)}
                              style={{ margin: 0 }}
                            />
                          )}
                          {mode === 'select' && (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                border: '2px solid #8b5cf6',
                                backgroundColor: 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: '#8b5cf6',
                                }}
                              />
                            </Box>
                          )}
                          {getFileIcon(document.file_type)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="subtitle2" noWrap>
                              {document.file_name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip 
                                label={getDocumentTypeLabel(document.document_type)} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                              <Chip
                                icon={document.is_public ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                                label={document.is_public ? 'Public' : 'Private'}
                                size="small"
                                color={document.is_public ? 'success' : 'default'}
                                variant="filled"
                                sx={{
                                  backgroundColor: document.is_public 
                                    ? alpha(theme.palette.success.main, 0.1) 
                                    : alpha(theme.palette.grey[500], 0.1),
                                  color: document.is_public 
                                    ? theme.palette.success.dark 
                                    : theme.palette.grey[600],
                                  border: `1px solid ${document.is_public 
                                    ? alpha(theme.palette.success.main, 0.3) 
                                    : alpha(theme.palette.grey[500], 0.3)}`,
                                  fontWeight: 500,
                                  '& .MuiChip-icon': {
                                    color: document.is_public 
                                      ? theme.palette.success.main 
                                      : theme.palette.grey[500],
                                  },
                                  '&:hover': {
                                    backgroundColor: document.is_public 
                                      ? alpha(theme.palette.success.main, 0.15) 
                                      : alpha(theme.palette.grey[500], 0.15),
                                  }
                                }}
                              />
                              {document.description && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {document.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(document.file_size)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              •
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(document.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View">
                            <IconButton 
                              size="small" 
                              onClick={() => viewDocument(document)}
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton 
                              size="small" 
                              onClick={() => downloadDocument(document)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {mode === 'view' && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleEditDocument(document)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="AI Processing">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleAIDocumentProcessing(document)}
                                  sx={{
                                    color: '#6366f1',
                                    '&:hover': {
                                      bgcolor: 'rgba(99, 102, 241, 0.1)',
                                    },
                                  }}
                                >
                                  <AIIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Share">
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    if (document.url) {
                                      navigator.clipboard.writeText(document.url);
                                      toast.success('Document link copied to clipboard');
                                    }
                                  }}
                                >
                                  <ShareIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton 
                                  size="small" 
                                  onClick={() => deleteDocument(document)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            </List>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>

      {/* Edit Document Dialog */}
      {editingDocument && (
        <Dialog
          open={!!editingDocument}
          onClose={() => setEditingDocument(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Document</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <TextField
                fullWidth
                label="File Name"
                value={editingDocument.file_name}
                disabled
                helperText="File name cannot be changed"
              />
              
              <FormControl fullWidth>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  label="Document Type"
                >
                  {DOCUMENT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                placeholder="Add a description for this document"
              />
              
              {/* Public/Private Toggle Card */}
              <Card sx={{ 
                p: 3, 
                bgcolor: isPublic ? 'rgba(76, 175, 80, 0.08)' : 'rgba(158, 158, 158, 0.08)',
                border: `2px solid ${isPublic ? 'rgba(76, 175, 80, 0.2)' : 'rgba(158, 158, 158, 0.2)'}`,
                borderRadius: 2,
                transition: 'all 0.3s ease-in-out'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: isPublic ? 'success.main' : 'grey.500',
                    color: 'white',
                    transition: 'all 0.3s ease-in-out'
                  }}>
                    {isPublic ? <PublicIcon /> : <LockIcon />}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ 
                      color: isPublic ? 'success.main' : 'grey.600',
                      transition: 'color 0.3s ease-in-out'
                    }}>
                      {isPublic ? 'Public Document' : 'Private Document'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isPublic 
                        ? 'This document can be seen by others in your profile' 
                        : 'This document is only visible to you'
                      }
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isPublic}
                        onChange={(e) => setIsPublic(e.target.checked)}
                        color="success"
                        sx={{
                          '& .MuiSwitch-thumb': {
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: isPublic ? 'success.main' : 'grey.400',
                          },
                        }}
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: 1.5,
                  p: 2,
                  bgcolor: isPublic ? 'rgba(76, 175, 80, 0.05)' : 'rgba(158, 158, 158, 0.05)',
                  borderRadius: 1,
                  border: `1px solid ${isPublic ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)'}`
                }}>
                  <InfoIcon sx={{ 
                    color: isPublic ? 'success.main' : 'grey.500',
                    fontSize: 20,
                    mt: 0.2,
                    flexShrink: 0
                  }} />
                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: isPublic ? 'success.dark' : 'grey.600',
                      fontWeight: 500,
                      mb: 0.5
                    }}>
                      {isPublic ? 'Public documents will appear in:' : 'Private documents are:'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                      {isPublic ? (
                        <>
                          • Your profile's "Documents" section<br />
                          • Visible to other users browsing your profile<br />
                          • Can be used in job applications and portfolios
                        </>
                      ) : (
                        <>
                          • Only visible to you<br />
                          • Not shown in your public profile<br />
                          • Still available for personal use and applications
                        </>
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Card>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  File Size: {formatFileSize(editingDocument.file_size)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Uploaded: {new Date(editingDocument.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingDocument(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateDocument} 
              variant="contained"
              disabled={!documentType}
            >
              Update Document
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* PDF Viewer Modal */}
      {viewerOpen && viewingDocument && (
        <Dialog
          open={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            setViewingDocument(null);
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              height: '90vh',
              maxHeight: '90vh',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            pb: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PictureAsPdfIcon color="error" />
              <Typography variant="h6" fontWeight="bold">
                {viewingDocument.file_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadDocument(viewingDocument)}
              >
                Download
              </Button>
              <IconButton 
                onClick={() => {
                  setViewerOpen(false);
                  setViewingDocument(null);
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ p: 0, height: '100%' }}>
            <Box sx={{ 
              height: '100%', 
              width: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {viewingDocument.url ? (
                <iframe
                  src={viewingDocument.url}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    flex: 1
                  }}
                  title={viewingDocument.file_name}
                />
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  p: 4
                }}>
                  <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                  <Typography variant="h6" color="error" gutterBottom>
                    Unable to load PDF
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    The document could not be loaded. Please try downloading it instead.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadDocument(viewingDocument)}
                  >
                    Download Document
                  </Button>
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* AI Document Processing Modal */}
      <AIDocumentProcessingModal
        open={aiModalOpen}
        onClose={() => {
          setAiModalOpen(false);
          setAiProcessingDocument(null);
        }}
        document={aiProcessingDocument}
      />
    </Dialog>
  );
};

export default UserDocumentsModal;
